import React, { FC, useEffect, useState } from 'react';

import { Button } from '@yandex-360/orb-components/dist/components/Button';
import { TooltipStateful } from '@yandex-360/orb-components/dist/components/Tooltip/TooltipStateful';
import { isMediaRecorderSupported, States } from '@yandex-360/telemost-sdk/audio-diagnostics';
import { LoggableError } from '@yandex-int/messenger.errors';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { i18n } from '../../../../../../../../helpers/i18n';
import { createLogger } from '../../../../../../../../helpers/loggerFacade';
import { useActions } from '../../../../../../../../hooks';
import {
    getAudioOutputDeviceSelector,
    selectIsAudioOutputDiagnosticRun,
    setIsAudioInputDiagnosticRunAction,
} from '../../../../../../../settings';
import { useInputMediaStreamContext } from '../../../../../../hooks/use-diagnostic-mic-stream';
import { useInputAudioHandler } from '../../../../../../hooks/use-input-audio-handler';
import { getInputDiagnosticBtnTitle } from '../../../../../utils';
import css from '../styles.css';
import { getInputDiagnosticBtnIcon } from './get-input-diagnostic-btn-icon';

interface InputDeviceDiagnosticButtonProps {
    isMobile?: boolean;
    isDisabled?: boolean;
}
const logger = createLogger('input-device-diagnostic-button');
const isRecordingSupported = isMediaRecorderSupported();
export const InputDeviceDiagnosticButton: FC<InputDeviceDiagnosticButtonProps> = ({
    isDisabled = false,
    isMobile = false,
}) => {
    const { setIsAudioInputDiagnosticRun } = useActions({
        setIsAudioInputDiagnosticRun: setIsAudioInputDiagnosticRunAction,
    });
    const currentOutputDeviceId = useSelector(getAudioOutputDeviceSelector)?.deviceId;
    const { mediaStream, mediaStreamIsReady } = useInputMediaStreamContext();
    const isOutputDiagnosticRun = useSelector(selectIsAudioOutputDiagnosticRun);
    const { audioHandlerState, audioHandlerRef } = useInputAudioHandler();

    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (!mediaStream || !audioHandlerRef.current) {
            return;
        }
        audioHandlerRef.current.changeInputDevice(mediaStream);
    }, [audioHandlerRef, mediaStream]);
    const onClick = () => {
        if (audioHandlerRef.current && mediaStream && mediaStream.active) {
            switch (audioHandlerState) {
                case States.IDLE: {
                    const deviceId = currentOutputDeviceId ?? '';
                    audioHandlerRef.current
                        .changeOutputDevice(deviceId)
                        .then(() => {
                            if (
                                audioHandlerRef.current &&
                                mediaStream &&
                                mediaStream.active &&
                                audioHandlerRef.current.state === States.IDLE
                            ) {
                                audioHandlerRef.current.startRecording(mediaStream);
                                setIsAudioInputDiagnosticRun(true);
                            } else {
                                if (!audioHandlerRef.current) {
                                    logger.error(
                                        new Error("Can't play record, audioHandler not defined") as LoggableError,
                                        {
                                            method: 'onClick',
                                            additional: {
                                                deviceId,
                                            },
                                        }
                                    );
                                    return;
                                }
                                if (!mediaStream || !mediaStream.active) {
                                    logger.error(
                                        new Error("Can't play record, mediaStream not active") as LoggableError,
                                        {
                                            method: 'onClick',
                                            additional: {
                                                deviceId,
                                            },
                                        }
                                    );
                                    return;
                                }
                            }
                        })
                        .catch((error) => {
                            logger.error(error, {
                                method: 'onClick',
                                additional: {
                                    deviceId,
                                },
                            });
                        });

                    break;
                }
                case States.RECORDING:
                    audioHandlerRef.current.stopRecording();
                    break;
                case States.PLAYING:
                    audioHandlerRef.current.stopPlayback();
                    break;
            }
        }
    };

    return (
        <div className={cn({ [css.isMobile]: isMobile, [css.diagnosticContainer]: isMobile })}>
            <TooltipStateful
                content={i18n('settings-v2', 'diagnostic-input-start-tooltip')}
                isDisabled={Boolean(audioHandlerState) && audioHandlerState !== States.IDLE}
                trigger="hover"
                className={css.inputDiagnosticButtonTooltip}
                placement="bottom right"
            >
                <Button
                    isDisabled={isDisabled || !isRecordingSupported || isOutputDiagnosticRun}
                    isPending={!isDisabled && !mediaStreamIsReady}
                    className={css.diagnosticBtn}
                    iconLeft={getInputDiagnosticBtnIcon(audioHandlerState ?? States.IDLE, isHovered)}
                    onPress={onClick}
                    onHoverChange={setIsHovered}
                >
                    {i18n('settings-v2', getInputDiagnosticBtnTitle(audioHandlerState ?? States.IDLE, isHovered))}
                </Button>
            </TooltipStateful>
        </div>
    );
};
